import { Link } from "react-router-dom";
import SEOHead from "@/components/layout/SEOHead";
import { ArrowUpRight, Heart, Music, Users, Sparkles, Star, MapPin } from "lucide-react";
import { PageHero } from "@/pages/About.hero";

// Real TIOB imagery
const IMG_COLLAGE = "/wp-content/uploads/2025/08/collage8.4-791x1024.jpeg";
const IMG_WELCOME = "/wp-content/uploads/2023/09/WEL-2-scaled.jpg";
const IMG_MEMBER = "/wp-content/uploads/2023/09/botom-pic2.jpg";
const IMG_FOLLOW = "/wp-content/uploads/2023/09/botom-pic3.jpg";
const IMG_TORAH = "/wp-content/uploads/2025/12/Rabbi-Holding-Torah.jpg";
const IMG_BBQ = "/wp-content/uploads/2026/05/ENDOFYEAROPENHOUSEANDBARBQ.png";

export default function About() {
  return (
    <>
      <SEOHead
        title="About Our Reform Synagogue in Brevard County"
        description="Learn about Temple Israel of Brevard, the Reform synagogue serving Melbourne, FL and the Space Coast since 1965. An all-inclusive, LGBTQ+ welcoming Jewish community in Brevard County, Florida."
        path="/about"
      />
      <PageHero
        eyebrow="About Temple Israel"
        title="Six decades of warmth, music, and tikkun olam."
        subtitle="From a small group of families on Merritt Island in 1965 to our home today in Viera, TIOB has always been a place where Jewish life is lived openly, joyfully, and together."
      />

      {/* SECTION 1 - Opening narrative + featured photo */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-4">★ Who We Are</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              A dynamic Reform Congregation - where everyone has a seat at the table.
            </h2>
            <div className="mt-7 space-y-5 text-[var(--ink-soft)] text-lg leading-relaxed">
              <p>
                Temple Israel of Brevard is a dynamic Reform Congregation formed on Merritt Island,
                Florida in 1965. We are a proud member of the{" "}
                <strong className="text-[var(--blue-ink)]">Union for Reform Judaism</strong>, and in
                2020 we relocated to our current home in the Viera region of Melbourne.
              </p>
              <p>
                We are <strong className="text-[var(--blue-ink)]">welcoming to all</strong> -
                including interfaith couples and families, LGBTQ+ individuals, Jews of color,
                singles, couples, seniors, snowbirds, and seekers of every kind. We want to be
                <em> your</em> Jewish community.
              </p>
              <p>
                If you have not visited us recently, come experience our{" "}
                <strong className="text-[var(--blue-ink)]">music-filled, vibrant Shabbat worship</strong>{" "}
                and meet our amazing rabbi. We can't wait to greet you at the door.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/worship" className="btn-primary" data-testid="about-worship-cta">
                Join us for Shabbat <ArrowUpRight size={18} />
              </Link>
              <Link to="/contact" className="btn-ghost">
                Plan a visit
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div aria-hidden="true" className="absolute -inset-4 bg-[var(--blue)]/10 rounded-[2rem] rotate-[-3deg]" />
            <img
              src={IMG_COLLAGE}
              alt="Snapshots of life at Temple Israel of Brevard"
              className="relative rounded-[2rem] shadow-xl w-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2 - By the Numbers */}
      <section className="bg-[var(--cream)] py-16 lg:py-20 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <p className="eyebrow text-center mb-6">★ TIOB By the Numbers</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            <Stat number="60+" label="Years serving Brevard" />
            <Stat number="1965" label="Founded on Merritt Island" />
            <Stat number="1" label="Brand-new home in Viera" />
            <Stat number="∞" label="Friendships forged here" />
          </div>
        </div>
      </section>

      {/* SECTION 3 - Story Timeline */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow mb-4">★ Our Story</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              A small congregation that grew into a thriving Jewish home.
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <img src={IMG_WELCOME} alt="A warm welcome at TIOB" className="rounded-3xl shadow-lg w-full" loading="lazy" />
            </div>
            <div className="lg:col-span-7">
              <ol className="relative border-l-2 border-[var(--blue)]/30 ml-3 space-y-10">
                <TimelineItem
                  year="1965"
                  title="Born on Merritt Island"
                  body="A handful of Brevard County families gathered with a dream of building a Reform Jewish home on Florida's Space Coast. With borrowed prayer books and unbreakable resolve, Temple Israel of Brevard was founded."
                />
                <TimelineItem
                  year="The Decades Between"
                  title="Generations of celebration"
                  body="B'nei mitzvah, weddings, baby namings, High Holy Days, Passover seders, religious-school classrooms full of laughter - the rhythm of Jewish life passed from generation to generation."
                />
                <TimelineItem
                  year="2020"
                  title="A new home in Viera"
                  body="TIOB relocated to its current home in the Viera region of Melbourne - a beautiful sanctuary, religious-school wing, social hall, and playground built for the next sixty years of Jewish life."
                />
                <TimelineItem
                  year="2023"
                  title="Rabbi Karen Fanwick joins us"
                  body="Rabbi Fanwick was hired to lead our spiritual life - bringing her music-filled worship, her Hebrew curriculum for adults, and her open-door approach to every congregant."
                />
                <TimelineItem
                  year="Today"
                  title="Active, inclusive, joyful"
                  body="Shabbat services, Torah study, Mahjong, Men's Club, religious school, BBQs and seders - the calendar is full and every seat at the table is open to you."
                  last
                />
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 - Photo mosaic */}
      <section className="bg-[var(--cream)] py-20 lg:py-28 border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">★ Snapshots</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Life at TIOB, in pictures.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img src={IMG_TORAH} alt="Rabbi Fanwick holding the Torah" className="rounded-2xl w-full h-72 md:h-80 object-cover shadow-md row-span-2" loading="lazy" />
            <img src={IMG_MEMBER} alt="TIOB members gathered" className="rounded-2xl w-full h-36 md:h-40 object-cover shadow-md" loading="lazy" />
            <img src={IMG_FOLLOW} alt="Community gathering" className="rounded-2xl w-full h-36 md:h-40 object-cover shadow-md" loading="lazy" />
            <img src={IMG_BBQ} alt="End-of-year BBQ flyer" className="rounded-2xl w-full h-72 md:h-80 object-cover shadow-md row-span-2" loading="lazy" />
            <img src={IMG_WELCOME} alt="Warm welcome at TIOB" className="rounded-2xl w-full h-36 md:h-40 object-cover shadow-md col-span-2" loading="lazy" />
          </div>
        </div>
      </section>

      {/* SECTION 5 - Who we welcome */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow mb-4">★ Who We Welcome</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              All means all. Truly.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] text-lg leading-relaxed">
              Walk through our doors and you'll find a community that has consciously, joyfully
              chosen to be a home for every kind of seeker. Wherever you are on your Jewish
              journey, you belong here.
            </p>
            <Link to="/contact" className="btn-warm mt-8" data-testid="about-welcome-cta">
              <Heart size={16} /> Reach out - we'd love to meet you
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Interfaith couples & families",
              "LGBTQ+ individuals",
              "Jews of color",
              "Singles & couples",
              "Seniors & snowbirds",
              "Jews by birth, choice, or discovery",
              "Newcomers to Brevard",
              "Lifelong learners",
            ].map((label) => (
              <div key={label} className="card-soft p-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--blue)]/10 text-[var(--blue-deep)] flex items-center justify-center shrink-0">
                  <Star size={16} className="fill-[var(--honey)] text-[var(--honey)]" />
                </div>
                <p className="text-[var(--blue-ink)] font-medium">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 - Quote */}
      <section className="bg-[var(--blue-ink)] py-20 lg:py-24 text-white relative overflow-hidden">
        <div aria-hidden="true" className="absolute -top-24 -left-24 w-96 h-96 bg-[var(--blue-bright)]/20 blob" />
        <div aria-hidden="true" className="absolute -bottom-24 -right-24 w-80 h-80 bg-[var(--honey)]/25 blob" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <p className="font-mono-accent text-xs tracking-[0.28em] uppercase text-[var(--blue-bright)] mb-5">
            ★ Pirke Avot 1:6
          </p>
          <blockquote className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            "Find yourself a rabbi, acquire for yourself a friend, and judge every person on the
            positive side."
          </blockquote>
        </div>
      </section>

      {/* SECTION 7 - Values */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">★ Our Values</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              What guides us, week after week.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <ValueCard
              icon={Music}
              title="Joyful Worship"
              text="Our Shabbat services are music-filled and heartfelt. Whether you sing along or simply listen, you'll feel the warmth."
            />
            <ValueCard
              icon={Users}
              title="Real Belonging"
              text="From Mahjong to Men's Club to Torah Study, we build community through shared time, shared food, and shared stories."
            />
            <ValueCard
              icon={Sparkles}
              title="Tikkun Olam"
              text="Repairing the world is not abstract here. It's the tzedakah we give, the neighbor we visit, the welcome we extend."
            />
          </div>
        </div>
      </section>

      {/* SECTION 8 - Visit CTA */}
      <section className="bg-[var(--cream)] py-20 lg:py-24 border-t border-[var(--line-warm)]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow mb-4">★ Plan Your First Visit</p>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--blue-ink)] leading-tight">
              Come as you are.
            </h2>
            <p className="mt-5 text-[var(--ink-soft)] text-lg">
              The best way to meet TIOB is in person. Friday-night Shabbat services start at 7:00 PM
              and visitors are always welcome - no reservation needed.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/rabbi" className="btn-primary" data-testid="about-meet-rabbi">
                Meet Rabbi Fanwick <ArrowUpRight size={16} />
              </Link>
              <Link to="/events" className="btn-ghost">
                See the calendar
              </Link>
            </div>
          </div>
          <div className="card-soft p-7 bg-white">
            <p className="eyebrow mb-3">★ Visit Us</p>
            <p className="font-display text-2xl text-[var(--blue-ink)] flex items-start gap-3">
              <MapPin className="text-[var(--blue)] mt-1 shrink-0" size={20} />
              <span>7300 Lake Andrew Drive<br />Melbourne, FL 32940</span>
            </p>
            <div className="mt-5 pt-5 border-t border-[var(--line)] text-[var(--ink-soft)] text-sm space-y-2">
              <p><strong className="text-[var(--blue-ink)]">Phone:</strong> <a href="tel:+13216319494" className="hover:text-[var(--blue-deep)]">(321) 631-9494</a></p>
              <p><strong className="text-[var(--blue-ink)]">Email:</strong> <a href="mailto:tioffice@tiofbrevard.com" className="hover:text-[var(--blue-deep)] break-all">tioffice@tiofbrevard.com</a></p>
              <p><strong className="text-[var(--blue-ink)]">Office Hours:</strong> Tue-Fri, 9 AM - 2 PM (by appointment)</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ number, label }) {
  return (
    <div className="text-center">
      <div className="font-display text-5xl md:text-6xl text-[var(--blue)] leading-none">{number}</div>
      <p className="mt-3 text-sm md:text-base text-[var(--ink-soft)] font-medium">{label}</p>
    </div>
  );
}

function TimelineItem({ year, title, body, last }) {
  return (
    <li className={`ml-6 ${last ? "" : "pb-2"}`}>
      <span className="absolute -left-[11px] w-5 h-5 rounded-full bg-[var(--blue)] border-4 border-white shadow" aria-hidden="true" />
      <p className="font-mono-accent text-xs uppercase tracking-widest text-[var(--blue-deep)]">{year}</p>
      <h3 className="font-display text-2xl text-[var(--blue-ink)] mt-1">{title}</h3>
      <p className="mt-2 text-[var(--ink-soft)] leading-relaxed">{body}</p>
    </li>
  );
}

function ValueCard({ icon: Icon, title, text }) {
  return (
    <div className="card-soft p-7 text-center">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--honey)]/25 text-[var(--blue-ink)] flex items-center justify-center mb-4">
        <Icon size={22} />
      </div>
      <h3 className="font-display text-2xl text-[var(--blue-ink)]">{title}</h3>
      <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{text}</p>
    </div>
  );
}
